import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import getText from 'context/language/getText';
import { serializeClasses } from 'utilities/css';
import { joinStrings } from 'utilities/functions/array';

// see components/layout/contentArea/styledContentContainer for
// base header styles

export const getHeaderTag = (level = undefined) => {
  const Tag = ({ children, ...rest }) => {
    switch (level) {
      // @TODO: remove style once MUI typography components come in
      case 1: return <h1 style={{ fontSize: '28px' }} {...rest}>{children}</h1>;
      case 2: return <h2 style={{ fontSize: '24px' }} {...rest}>{children}</h2>;
      case 3: return <h3 style={{ fontSize: '20px' }} {...rest}>{children}</h3>;
      case 4: return <h4 style={{ fontSize: '18px' }} {...rest}>{children}</h4>;
      case 5: return <h5 style={{ fontSize: '16px' }} {...rest}>{children}</h5>;
      case 6: return <h6 style={{ fontSize: '14px' }} {...rest}>{children}</h6>;
      default:
        return <div {...rest}>{children}</div>;
    }
  };
  Tag.propTypes = {
    children: PropTypes.node.isRequired
  };
  return Tag;
};

export const makeClassName = (inline, rest) => {
  const inlineClass = inline ? 'inline' : '';
  const { className } = rest;
  return serializeClasses([inlineClass, className]);
};

export const getDisplayText = (textPath, options = undefined, optional = false) => {
  const text = getText(textPath, options);
  const optionalText = optional && `(${getText('common.optional')})`;
  const displayText = joinStrings([text, optionalText]);
  return displayText;
};

const BaseHeader = ({
  textPath,
  options,
  optional,
  level,
  inline,
  children,
  ...rest
}) => {
  const Tag = getHeaderTag(level);
  const className = makeClassName(inline, rest);
  if (textPath === undefined) {
    return <Tag {...rest} className={className}>{children}</Tag>;
  }
  const displayText = getDisplayText(textPath, options, optional);
  return <Tag {...rest} className={className}>{displayText}</Tag>;
};

BaseHeader.propTypes = {
  optional: PropTypes.bool,
  textPath: PropTypes.string,
  options: PropTypes.object,
  level: PropTypes.number,
  inline: PropTypes.bool,
  children: PropTypes.node,
};

BaseHeader.defaultProps = {
  optional: false,
  textPath: undefined,
  options: undefined,
  level: undefined,
  inline: true,
  children: undefined
};

const Header = styled(BaseHeader)`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.headingBlacker};
  &.inline {
    display: inline-block;
  }
`;

export const LargeHeader = styled(Header)`
  height: 32px;
  font-size: 28px;
  line-height: 1.19;
`;

export const MediumHeader = styled(Header)`
  height: 25px;
  font-size: 21px;
  line-height: 1.14;
`;

export const SmallHeader = styled(Header)`
  height: 20px;
  font-size: 14px;
  text-align: center;
`;

export default Header;
